import React, { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, LabelList } from "recharts";
import DeploymentModal from './DeploymentModal';
import { generateCareerPathHTML } from '../utils/careerPathTemplateLoader';

/************************************
 * AI Career Pathfinder – Enhanced with Writing Tasks & Tips
 * - Business writing tasks rotate for practice
 * - Model answer adapts to CEFR level
 * - AI Impact Tips filled in (Step 2)
 ************************************/

const CEFR_LEVELS = ["A1", "A2", "B1", "B2"];
const MBTI_LIST = ["INTJ", "INTP", "ENTJ", "ENTP", "INFJ", "INFP", "ENFJ", "ENFP", "ISTJ", "ISFJ", "ESTJ", "ESFJ", "ISTP", "ISFP", "ESTP", "ESFP"];
const ACTIVITIES = ["Scratch game", "school science fair", "YouTube video editing", "café part-time", "Robotics club", "Art/design", "Sports team", "Volunteering", "Store sales", "Blog writing", "Math study group"];
const CLUSTERS = ["AI Engineer", "Data Scientist", "Product Manager (Tech)", "UI Designer"];

const TASKS = [
    "Email your manager to request a project deadline extension.",
    "Write to a customer introducing a new product feature.",
    "Ask a professor for a recommendation letter.",
    "Invite colleagues to a weekly team sync meeting.",
    "Follow up with a client after a demo call."
];

function makeBarData(mbti, acts) {
    const base = { "AI Engineer": 6, "Data Scientist": 5, "Product Manager (Tech)": 4, "UI Designer": 4 };
    if (["INTJ", "INTP", "ENTJ", "ENTP"].includes(mbti)) { base["AI Engineer"] += 3; base["Data Scientist"] += 2; }
    if (["ENFP", "ENFJ", "ENTP", "ESTP"].includes(mbti)) { base["Product Manager (Tech)"] += 3; }
    if (["ISFP", "INFP", "ENFP", "ESFP"].includes(mbti)) { base["UI Designer"] += 3; }
    const text = acts.join(" ").toLowerCase();
    if (/(robot|coding|science|robotics)/.test(text)) base["AI Engineer"] += 3;
    if (/(math|data|blog)/.test(text)) base["Data Scientist"] += 2;
    if (/(store|sales|fair)/.test(text)) base["Product Manager (Tech)"] += 2;
    if (/(design|art|youtube|video)/.test(text)) base["UI Designer"] += 3;
    return CLUSTERS.map(c => ({ cluster: c, Personality: Math.max(0, Math.round(base[c] * 0.6)), Activities: Math.max(0, base[c] - Math.round(base[c] * 0.6)), Total: base[c] }));
}

function modelAnswer(cefr, task) {
    const samples = {
        A1: `Hello. I want ${task}. Thank you.`,
        A2: `Hello, I need to ${task.toLowerCase()}. I am free on Tuesday or Thursday. Thank you.`,
        B1: `Hi, I would like to ${task.toLowerCase()} this week. I am available Tuesday 3 pm or Thursday 5 pm.`,
        B2: `Dear Team, I’d like to ${task.toLowerCase()} Please let me know a convenient time. Thanks in advance.`
    };
    return samples[cefr] || samples.B1;
}

export default function App() {
    const [step, setStep] = useState(1);
    const [recommendedPaths, setRecommendedPaths] = useState(CLUSTERS);
    const [mbti, setMbti] = useState("INTP");
    const [cefr, setCefr] = useState("B2");
    const [acts, setActs] = useState(["Art/design"]);
    const [studentText, setStudentText] = useState("");
    const [taskIndex, setTaskIndex] = useState(0);
    
    // DeploymentModal 관련 state
    const [showDeploymentModal, setShowDeploymentModal] = useState(false);
    const [deploymentData, setDeploymentData] = useState({
        htmlContent: '',
        fileName: '',
        contentType: ''
    });

    const barData = useMemo(() => makeBarData(mbti, acts), [mbti, acts]);
    const toggle = (a) => setActs(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

    const currentTask = TASKS[taskIndex % TASKS.length];
    
    // 배포 기능
    const handleDeployment = () => {
        try {
            // HTML 생성
            const htmlContent = generateCareerPathHTML(''); // 기본 시트 URL 사용
            
            // 파일명 생성 (타임스탬프 포함)
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
            const fileName = `ai-career-path-${mbti.toLowerCase()}-${cefr.toLowerCase()}-${timestamp}.html`;
            
            // DeploymentModal 데이터 설정
            setDeploymentData({
                htmlContent: htmlContent,
                fileName: fileName,
                contentType: 'templates'
            });
            
            // DeploymentModal 열기
            setShowDeploymentModal(true);
        } catch (error) {
            console.error('배포 준비 중 오류:', error);
            alert('배포 준비 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-600 via-violet-600 to-fuchsia-600 text-white">
            <header className="sticky top-0 z-10 backdrop-blur bg-white/10 border-b border-white/20">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <h1 className="text-2xl font-bold">AI Career Pathfinder</h1>
                    <div className="text-xs/relaxed text-white/80">VOCATIONAL ENGLISH • AI‑READY</div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
                {/* Step 1 */}
                {step === 1 && (
                    <section className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-2xl p-5 shadow">
                            <h2 className="text-xl font-semibold">1) Your Profile</h2>
                            <div className="bg-white/95 rounded-2xl p-4 text-slate-900 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium">MBTI</label>
                                    <select className="mt-1 w-full border rounded-lg p-2" value={mbti} onChange={e => setMbti(e.target.value)}>
                                        {MBTI_LIST.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">CEFR Level</label>
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                        {CEFR_LEVELS.map(l => (
                                            <button key={l} onClick={() => setCefr(l)} className={`px-3 py-1 rounded-full border ${cefr === l ? 'bg-purple-600 text-white' : 'bg-white text-slate-800'}`}>{l}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">Activities</label>
                                    <div className="grid grid-cols-2 gap-2 mt-1">
                                        {ACTIVITIES.map(tag => (
                                            <button key={tag} onClick={() => toggle(tag)} className={`px-2 py-1 rounded-lg text-sm ${acts.includes(tag) ? 'bg-purple-600 text-white' : 'bg-white border'}`}>{tag}</button>
                                        ))}
                                    </div>
                                </div>
                                <button className="w-full py-2 rounded-xl bg-purple-700 text-white font-semibold" onClick={() => {
                                    const sorted = barData.slice().sort((a, b) => b.Total - a.Total).map(d => d.cluster);
                                    setRecommendedPaths(sorted);
                                    setStep(2);
                                }}>Generate My Path ▸</button>
                            </div>
                        </div>
                        <div className="bg-white/10 rounded-2xl p-5 shadow">
                            <h3 className="text-xl font-semibold mb-2">Career Cluster Preview</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={barData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="cluster" />
                                    <YAxis allowDecimals={false} />
                                    <RTooltip />
                                    <Legend />
                                    <Bar dataKey="Personality" stackId="a" fill="#7C3AED">
                                        <LabelList dataKey="Total" position="top" />
                                    </Bar>
                                    <Bar dataKey="Activities" stackId="a" fill="#22C55E" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </section>
                )}

                {/* Step 2 */}
                {step === 2 && (
                    <section className="bg-white/10 rounded-2xl p-5 shadow">
                        <h2 className="text-xl font-semibold mb-3">2) Your AI‑tailored Path</h2>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-white/90 text-slate-900 rounded-2xl p-4">
                                <div className="font-semibold mb-2">Recommended Paths</div>
                                <ol className="space-y-2 text-sm">
                                    {recommendedPaths.map((c, i) => (<li key={c}><span className="text-purple-700 font-bold">{i + 1}.</span> {c}</li>))}
                                </ol>
                            </div>
                            <div className="bg-white/90 text-slate-900 rounded-2xl p-4">
                                <div className="font-semibold mb-2">AI Impact Tips</div>
                                <ul className="list-disc list-inside text-sm space-y-1">
                                    <li>AI Engineer: Learn prompt design and model limits</li>
                                    <li>AI Engineer: Build small AI tools to solve problems</li>
                                    <li>AI Engineer: Stay ethical with data</li>
                                    <li>Data Scientist: Use AI for fast data cleaning</li>
                                </ul>
                            </div>
                            <div className="bg-white/90 text-slate-900 rounded-2xl p-4">
                                <div className="font-semibold mb-2">English in your Field</div>
                                <ul className="list-disc list-inside text-sm space-y-1">
                                    <li>AI Engineer: Read docs and papers</li>
                                    <li>AI Engineer: Write clear commit messages</li>
                                    <li>AI Engineer: Explain systems to non-experts</li>
                                    <li>Data Scientist: Write reports</li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-between">
                            <button className="px-4 py-2 rounded bg-white/20" onClick={() => setStep(1)}>◂ Back</button>
                            <button className="px-4 py-2 rounded bg-white text-purple-700" onClick={() => setStep(3)}>Next ▸</button>
                        </div>
                    </section>
                )}

                {/* Step 3 */}
                {step === 3 && (
                    <>
                        <section className="bg-white/10 rounded-2xl p-5 shadow">
                            <h2 className="text-xl font-semibold mb-3">Role Models Like You</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="bg-white/90 text-slate-900 rounded-2xl p-4 flex items-start gap-3">
                                    <img src="https://i.pravatar.cc/100?img=1" alt="Minho" className="w-16 h-16 rounded-full" />
                                    <div>
                                        <div className="font-semibold">Minho</div>
                                        <div className="text-xs text-slate-600">AI Engineer • INTJ</div>
                                        <div className="mt-2 text-sm bg-slate-50 border rounded-xl p-2">Hi! I’m Minho. As an AI Engineer, keep coding daily, read docs, and share your work with peers.</div>
                                    </div>
                                </div>
                                <div className="bg-white/90 text-slate-900 rounded-2xl p-4 flex items-start gap-3">
                                    <img src="https://i.pravatar.cc/100?img=2" alt="Dohyun" className="w-16 h-16 rounded-full" />
                                    <div>
                                        <div className="font-semibold">Dohyun</div>
                                        <div className="text-xs text-slate-600">Product Manager (Tech) • ENTP</div>
                                        <div className="mt-2 text-sm bg-slate-50 border rounded-xl p-2">Hi! I’m Dohyun. As a PM, focus on communication, teamwork, and translating tech into simple terms.</div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="bg-white/10 rounded-2xl p-5 shadow space-y-3">
                            <h2 className="text-xl font-semibold mb-3">Vocational English Practice</h2>
                            <div className="bg-white/90 text-slate-900 rounded-xl p-3 text-sm">
                                <div className="font-medium mb-1">Your Prompt</div>
                                <div>{currentTask}</div>
                                <button className="mt-2 px-3 py-1 rounded bg-purple-600 text-white" onClick={() => setTaskIndex(taskIndex + 1)}>Next Task</button>
                            </div>
                            <div className="bg-white/90 text-slate-900 rounded-xl p-3 text-sm">
                                <div className="font-medium mb-1">Model Answer (CEFR {cefr})</div>
                                <div>{modelAnswer(cefr, currentTask)}</div>
                            </div>
                            <textarea className="w-full h-40 rounded-xl p-3 text-slate-900" placeholder="Write your response here…" value={studentText} onChange={e => setStudentText(e.target.value)} />
                            {studentText && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {["Grammar", "Vocabulary", "Clarity", "Task"].map((lab, i) => {
                                        const vals = { Grammar: 80, Vocabulary: 100, Clarity: 85, Task: 67 };
                                        const colors = { Grammar: "bg-blue-500", Vocabulary: "bg-green-500", Clarity: "bg-purple-500", Task: "bg-red-500" };
                                        return (
                                            <div key={lab} className="p-3 bg-white/90 rounded-xl text-slate-900">
                                                <div className="text-sm font-medium">{lab}</div>
                                                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                                    <div className={`h-full ${colors[lab]}`} style={{ width: `${vals[lab]}%` }} />
                                                </div>
                                                <div className="mt-1 text-xs">{vals[lab]}%</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                        
                        {/* 배포 버튼 섹션 */}
                        <section className="bg-white/10 rounded-2xl p-5 shadow">
                            <h2 className="text-xl font-semibold mb-3">🚀 학습 자료 배포</h2>
                            <p className="text-white/80 mb-4">진로 분석 결과를 HTML 파일로 저장하거나 GitHub Pages에 배포할 수 있습니다.</p>
                            <div className="flex justify-center">
                                <button 
                                    onClick={handleDeployment}
                                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300"
                                >
                                    📋 결과 배포하기
                                </button>
                            </div>
                        </section>
                    </>
                )}
            </main>
            
            {/* DeploymentModal */}
            <DeploymentModal
                isOpen={showDeploymentModal}
                htmlContent={deploymentData.htmlContent}
                fileName={deploymentData.fileName}
                contentType={deploymentData.contentType}
                enableLiveShare={true}
                onClose={() => setShowDeploymentModal(false)}
            />
        </div>
    );
}
